type PermissionMode = "read" | "readwrite";

interface PermissionCapableHandle {
  queryPermission?: (descriptor: { mode: PermissionMode }) => Promise<PermissionState>;
  requestPermission?: (descriptor: { mode: PermissionMode }) => Promise<PermissionState>;
}

export function isAutoSaveSupported(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

export async function pickDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (!isAutoSaveSupported()) {
    return null;
  }
  try {
    const picker = (
      window as unknown as {
        showDirectoryPicker: (opts?: {
          mode?: PermissionMode;
        }) => Promise<FileSystemDirectoryHandle>;
      }
    ).showDirectoryPicker;
    return await picker({ mode: "readwrite" });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return null;
    }
    throw err;
  }
}

export async function verifyPermission(
  handle: FileSystemDirectoryHandle,
  mode: PermissionMode = "readwrite",
): Promise<boolean> {
  const h = handle as FileSystemDirectoryHandle & PermissionCapableHandle;
  const current = await h.queryPermission?.({ mode });
  if (current === "granted") {
    return true;
  }
  const requested = await h.requestPermission?.({ mode });
  return requested === "granted";
}

export async function writeBlobToHandle(
  dirHandle: FileSystemDirectoryHandle,
  filename: string,
  blob: Blob,
): Promise<void> {
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

export function buildAutoSaveFilename(
  patientId: string,
  category: string,
  testType: string,
  dateMs: number,
  eyePrefix: string | null = null,
): string {
  const safe = (value: string): string => value.replace(/[^A-Za-z0-9_-]+/g, "-");
  const date = new Date(dateMs);
  const pad = (n: number): string => String(n).padStart(2, "0");
  const stamp =
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `_${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}`;
  const parts = [
    eyePrefix ? safe(eyePrefix) : null,
    safe(patientId),
    safe(category),
    safe(testType),
    stamp,
  ].filter((part): part is string => Boolean(part));
  return `${parts.join("_")}.pdf`;
}
